import { BLOCS, libelleBloc, libelleChamp, libelleValeur } from '../../src/partage/libelles-formulaire';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * LE FORMULAIRE D'ÉTAT, EN CLAIR — un seul rendu pour deux écrans.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ⚠️⚠️ CE QU'IL REMPLACE : `<pre>{JSON.stringify(d.formulaire, null, 2)}</pre>`.
 * L'agent qui CERTIFIE l'acte lisait des clés de base de données dans l'ordre
 * du sérialiseur. Mesuré sur GN-DEM-000000033 le 24/09/2026 :
 * `"communeOuSousPrefecture": "matoto"`, `"prenoms": "N'faly"`, sur le poste
 * d'un chef de service foncier.
 *
 * ⚠️ MÊME FONCTION AVANT ET APRÈS LE DÉPÔT. Le récapitulatif lit le corps que
 * `construire()` va envoyer ; la fiche lit `fon.demande.formulaire`, qui est ce
 * même corps tel qu'il a été reçu. Deux rendus divergeraient, et l'agent
 * relirait à l'écran autre chose que ce qu'il dépose.
 *
 * ⚠️ RIEN N'EST TU. Une clé absente du catalogue est rendue avec son nom brut
 * et signalée ; un champ vide est rendu « à compléter » et non sauté. Un
 * récapitulatif qui omet ce qu'il ne sait pas lire fait certifier un acte
 * amputé — et c'est plus grave que le JSON qu'il remplace.
 */

type Bloc = { cle: string; titre: string; valeur: unknown };

/** Les blocs, dans l'ordre de l'acte ; les clés inconnues ferment la marche. */
function ordonner(formulaire: Record<string, unknown>): Bloc[] {
  const connus = BLOCS.map(([c]) => c);
  const cles = [
    ...connus.filter((c) => c in formulaire),
    ...Object.keys(formulaire).filter((c) => !connus.includes(c)),
  ];
  return cles.map((cle) => ({ cle, titre: libelleBloc(cle), valeur: formulaire[cle] }));
}

const estObjet = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

function Ligne({ cle, valeur, profondeur = 0 }: { cle: string; valeur: unknown; profondeur?: number }) {
  const label = libelleChamp(cle);

  if (Array.isArray(valeur)) {
    if (valeur.length === 0) {
      return <div className="ligne-fiche"><span>{label}</span><strong className="absent">aucun</strong></div>;
    }
    return (
      <>
        {valeur.map((v, i) => (
          <div key={i} className="lisible-sous">
            <div className="lisible-sous-titre">{label} n° {i + 1}</div>
            {estObjet(v)
              ? Object.entries(v).map(([k, x]) => <Ligne key={k} cle={k} valeur={x} profondeur={profondeur + 1} />)
              : <div className="ligne-fiche"><strong>{String(v)}</strong></div>}
          </div>
        ))}
      </>
    );
  }

  if (estObjet(valeur)) {
    /* ⚠️ `personne` et `adresse` sont des enveloppes : on aplatit plutôt que
       d'empiler un titre par niveau — un récapitulatif à quatre indentations
       ne se lit plus, et c'est un document que l'on certifie. */
    const enveloppe = cle === 'personne' || cle === 'adresse' || cle === 'siegeAdresse';
    const corps = Object.entries(valeur).map(([k, x]) =>
      <Ligne key={k} cle={k} valeur={x} profondeur={profondeur + 1} />);
    if (enveloppe) return <>{corps}</>;
    return (
      <div className="lisible-sous">
        <div className="lisible-sous-titre">{label}</div>
        {corps}
      </div>
    );
  }

  const texte = libelleValeur(cle, valeur);
  /* ⚠️ UN CHAMP QUE LE CATALOGUE NE CONNAÎT PAS EST MARQUÉ, PAS SAUTÉ.
     `libelleChamp` rend la clé elle-même quand elle lui est inconnue : c'est
     le signe, et il est rendu à l'écran. Omettre ces champs ferait relire —
     et certifier — un acte amputé sans que rien ne le dise. */
  const inconnu = libelleChamp(cle) === cle;
  return (
    <div className="ligne-fiche">
      <span>{label}{inconnu && <b className="lisible-inconnu" title="Champ absent du catalogue des libellés">?</b>}</span>
      {texte === null
        ? <strong className="absent">à compléter</strong>
        : <strong>{texte}</strong>}
    </div>
  );
}

export function FormulaireLisible(
  { formulaire, onModifier, peutModifier, titreModifier }:
  {
    formulaire: Record<string, unknown>;
    /** Ramène au cadre qui porte ce bloc. Absent : lecture seule (certification). */
    onModifier?: (cle: string) => void;
    /**
     * ⚠️ UN BOUTON QUI NE MÈNE NULLE PART EST PIRE QUE PAS DE BOUTON. Certains
     * blocs du corps déposé ne correspondent à AUCUN cadre — `piecesJointes`,
     * les déclarations dispersées sur plusieurs cadres. Sans ce filtre, leur
     * « Modifier » ramènerait au cadre 1 par défaut, et l'agent conclurait que
     * la navigation est cassée.
     */
    peutModifier?: (cle: string) => boolean;
    titreModifier?: string;
  },
) {
  const blocs = ordonner(formulaire);
  if (blocs.length === 0) {
    return <p className="note-indispo">Ce formulaire ne porte aucun bloc — il n’a pas été construit.</p>;
  }
  return (
    <div className="lisible">
      {blocs.map((b) => (
        <section key={b.cle} className="lisible-bloc">
          <header className="lisible-tete">
            <h4>{b.titre}</h4>
            {onModifier && (peutModifier ? peutModifier(b.cle) : true) && (
              <button type="button" className="bouton discret" title={titreModifier ?? `Revenir à « ${b.titre} »`}
                      onClick={() => onModifier(b.cle)}>Modifier</button>
            )}
          </header>
          <div className="lisible-corps">
            {/* ⚠️ LE BLOC NE SE RÉPÈTE PAS EN SOUS-TITRE. Passer l'objet à
                `Ligne` l'enveloppait d'un titre portant son propre nom — « Le
                demandeur » puis « DEMANDEUR » juste dessous. */}
            {estObjet(b.valeur)
              ? Object.entries(b.valeur).map(([k, v]) => <Ligne key={k} cle={k} valeur={v} />)
              : Array.isArray(b.valeur)
              ? <Ligne cle={b.cle} valeur={b.valeur} />
              : <div className="ligne-fiche">
                  <span>{b.titre}</span>
                  {libelleValeur(b.cle, b.valeur) === null
                    ? <strong className="absent">à compléter</strong>
                    : <strong>{libelleValeur(b.cle, b.valeur)}</strong>}
                </div>}
          </div>
        </section>
      ))}
    </div>
  );
}
